/**
 * A table's column widths, remembered per table in this browser.
 *
 * The hook `DataTable` uses for every table it draws, and the one a table built
 * by hand reaches for: give it the table's key and its column keys in order,
 * put `headRef` on the header row, and render a `ResizeHandle` with
 * `handle(index)` inside each header cell. The arithmetic is in
 * `column-width-model.ts`, where it is tested.
 *
 * THE DRAWN WIDTHS ARE MEASURED, NOT ASSUMED. The header row is measured after
 * every render and whenever it changes size, so a handle always reports the
 * width a person can see, and a drag starts from it. Nothing is stored until a
 * drag or a key press settles (`commit`), so a drag across the screen is one
 * write rather than a few hundred, the way the inbox's panes do it.
 *
 * A per-viewer convenience, so `localStorage` and never the URL, and every read
 * and write swallows a blocked storage rather than letting it take the page down.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import {
  columnHandleBounds,
  columnWidthStore,
  columnWidthStyle,
  fitColumnWidths,
  readColumnWidths,
  resizeColumn,
  writeColumnWidths,
  type ColumnWidths,
} from './column-width-model'
import type { PaneBounds } from './pane-width-model'

/** What a `ResizeHandle` needs from a column, less its label. */
export interface ColumnHandle {
  width: number
  bounds: PaneBounds
  direction: 1
  edge: 'end'
  onResize: (width: number) => void
  onCommit: (width: number) => void
  onReset: () => void
  resetHint: string
}

export interface ColumnWidthsApi {
  /** Goes on the header row. Every cell in it is measured. */
  headRef: (row: HTMLTableRowElement | null) => void
  /** Whether a width is stored, which is when the table draws fixed widths. */
  resized: boolean
  /** On the table: fixed layout once a width is stored. */
  tableClassName: string | undefined
  /** On a scrolling table: its own width, so the columns add up and the card scrolls. */
  tableStyle: CSSProperties | undefined
  /** On a header cell: its stored width, once there is one. */
  columnStyle: (key: string) => CSSProperties | undefined
  /** The handle for the column at `index`, or null for a column without one. */
  handle: (index: number) => ColumnHandle | null
  /** Every column back to the browser's own layout, and the stored widths forgotten. */
  reset: () => void
}

/** Said in the handle's tooltip after "Double click". */
export const COLUMN_RESET_HINT = 'puts every column back'

interface Measured {
  /** The drawn width of each keyed column, in order. */
  columns: number[]
  /** The drawn width of the cells after them, which the table sizes itself: a row's actions. */
  extras: number
  /** The room the keyed columns have: the card, less the extras. Null until known. */
  available: number | null
}

const UNMEASURED: Measured = { columns: [], extras: 0, available: null }

function sameMeasure(a: Measured, b: Measured): boolean {
  return (
    a.available === b.available &&
    a.extras === b.extras &&
    a.columns.length === b.columns.length &&
    a.columns.every((width, at) => width === b.columns[at])
  )
}

function measureRow(row: HTMLTableRowElement, count: number): Measured {
  const widths = Array.from(row.cells, (cell) => Math.round(cell.getBoundingClientRect().width))
  const columns = widths.slice(0, count)
  const extras = widths.slice(count).reduce((total, width) => total + width, 0)
  // The card is the table's parent in every table here (`TableContainer`), and
  // its client width is the room, whether or not the table has overflowed it.
  const card = row.closest('table')?.parentElement ?? null
  const available = card === null ? null : Math.round(card.clientWidth) - extras
  return { columns, extras, available }
}

export function useColumnWidths(tableKey: string, keys: readonly string[], fit: boolean): ColumnWidthsApi {
  // Held by value: a caller mapping its keys inline hands over a new array every render.
  const joined = keys.join('\u0000')
  const stableKeys = useMemo(() => joined.split('\u0000').filter((key) => key !== ''), [joined])

  const [widths, setWidths] = useState<ColumnWidths>(() => readColumnWidths(columnWidthStore(), tableKey))
  useEffect(() => {
    setWidths(readColumnWidths(columnWidthStore(), tableKey))
  }, [tableKey])

  const [row, setRow] = useState<HTMLTableRowElement | null>(null)
  const [measured, setMeasured] = useState<Measured>(UNMEASURED)
  const measuredRef = useRef<Measured>(UNMEASURED)
  measuredRef.current = measured

  const measure = useCallback(() => {
    if (row === null) return
    const next = measureRow(row, stableKeys.length)
    if (!sameMeasure(measuredRef.current, next)) {
      measuredRef.current = next
      setMeasured(next)
    }
  }, [row, stableKeys])

  // After every render, so a column that changed with its data is read before
  // anybody reaches for a handle. The equality check above is what stops this
  // from rendering again when nothing moved.
  useLayoutEffect(measure)

  // And whenever the row or its card changes size without a render: a window
  // dragged narrower, a sidebar folded away.
  useEffect(() => {
    if (row === null || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(row)
    const card = row.closest('table')?.parentElement
    if (card) observer.observe(card)
    return () => observer.disconnect()
  }, [row, measure])

  const resized = Object.keys(widths).length > 0

  const commit = useCallback(
    (next: ColumnWidths) => {
      setWidths(next)
      writeColumnWidths(columnWidthStore(), tableKey, next)
    },
    [tableKey],
  )
  const reset = useCallback(() => commit({}), [commit])

  // The drawn widths a drag started from, held until it settles, so every frame
  // of the drag is worked out from the same picture rather than from the frame
  // before it.
  const base = useRef<number[] | null>(null)
  const begin = useCallback((): number[] => {
    if (base.current === null) base.current = [...measuredRef.current.columns]
    return base.current
  }, [])

  const rendered = useMemo(
    () => (resized ? fitColumnWidths(widths, stableKeys, fit ? measured.available : null) : null),
    [resized, widths, stableKeys, fit, measured.available],
  )

  const columnStyle = useCallback(
    (key: string): CSSProperties | undefined => {
      if (rendered === null) return undefined
      const at = stableKeys.indexOf(key)
      const width = rendered[at]
      return width === undefined ? undefined : columnWidthStyle(width)
    },
    [rendered, stableKeys],
  )

  const tableStyle = useMemo<CSSProperties | undefined>(() => {
    if (rendered === null || fit) return undefined
    return { width: `${String(rendered.reduce((total, width) => total + width, 0) + measured.extras)}px` }
  }, [rendered, fit, measured.extras])

  const handle = useCallback(
    (index: number): ColumnHandle | null => {
      const bounds = columnHandleBounds(measured.columns, index, fit)
      const width = measured.columns[index]
      if (bounds === null || width === undefined) return null
      return {
        width,
        bounds,
        direction: 1,
        edge: 'end',
        onResize: (next) => setWidths(resizeColumn(begin(), stableKeys, index, next, fit)),
        onCommit: (next) => {
          const from = begin()
          base.current = null
          commit(resizeColumn(from, stableKeys, index, next, fit))
        },
        onReset: reset,
        resetHint: COLUMN_RESET_HINT,
      }
    },
    [measured, fit, stableKeys, begin, commit, reset],
  )

  return {
    headRef: setRow,
    resized,
    tableClassName: resized ? 'table-fixed' : undefined,
    tableStyle,
    columnStyle,
    handle,
    reset,
  }
}
